import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isThisWeek, isToday, isYesterday, parseISO } from 'date-fns';
import type { NotificationItem } from '@/api/notifications/notificationsApi.types';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ListPagination } from '@/components/admin/shared/ListPagination';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import {
  NOTIFICATION_CENTER_PAGE_SIZE_VALUE,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
  useNotificationsPageQuery,
  useUnreadNotificationCountQuery,
} from '@/hooks/useNotifications';
import { resolveInAppNavigationPath } from '@/utils/notificationNavigation';
import { cn } from '@/lib/utils';
import { CategoryFilterChips } from './CategoryFilterChips';
import { NotificationRow } from './NotificationRow';
import {
  resolveNotificationCategoriesForScope,
  type NotificationCategory,
} from './notificationPresentation';
import { useAuth } from '@/hooks/useAuth';

const WEEK_STARTS_ON_MONDAY = { weekStartsOn: 1 } as const;

type DateGroupKey = 'today' | 'yesterday' | 'thisWeek' | 'older';

const DATE_GROUP_ORDER: DateGroupKey[] = ['today', 'yesterday', 'thisWeek', 'older'];

function resolveDateGroupKey(createdAt: string): DateGroupKey {
  const createdAtDate = parseISO(createdAt);
  if (isToday(createdAtDate) === true) {
    return 'today';
  }
  if (isYesterday(createdAtDate) === true) {
    return 'yesterday';
  }
  if (isThisWeek(createdAtDate, WEEK_STARTS_ON_MONDAY) === true) {
    return 'thisWeek';
  }
  return 'older';
}

function groupNotificationsByDate(
  notifications: NotificationItem[]
): Map<DateGroupKey, NotificationItem[]> {
  const groups = new Map<DateGroupKey, NotificationItem[]>();
  notifications.forEach(function assignToGroup(notification) {
    const groupKey = resolveDateGroupKey(notification.createdAt);
    const existingGroup = groups.get(groupKey);
    if (existingGroup === undefined) {
      groups.set(groupKey, [notification]);
    } else {
      existingGroup.push(notification);
    }
  });
  return groups;
}

export function NotificationCenterPage() {
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();
  const { activeScopeCode } = useAuth();
  const [page, setPage] = useState(1);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<NotificationCategory | null>(null);
  const notificationsQuery = useNotificationsPageQuery(page, unreadOnly, selectedCategory);
  const unreadCountQuery = useUnreadNotificationCountQuery();
  const markReadMutation = useMarkNotificationReadMutation();
  const markAllReadMutation = useMarkAllNotificationsReadMutation();

  const notificationPage = notificationsQuery.data;
  const notifications = notificationPage === undefined ? [] : notificationPage.items;
  const totalItems = notificationPage === undefined ? 0 : notificationPage.totalItems;
  const unreadCount = unreadCountQuery.data === undefined ? 0 : unreadCountQuery.data;

  const groupedNotifications = useMemo(
    function computeGroups() {
      const items = notificationPage === undefined ? [] : notificationPage.items;
      return groupNotificationsByDate(items);
    },
    [notificationPage]
  );

  function selectUnreadFilter(nextUnreadOnly: boolean) {
    setUnreadOnly(nextUnreadOnly);
    setPage(1);
  }

  function selectCategory(nextCategory: NotificationCategory | null) {
    setSelectedCategory(nextCategory);
    setPage(1);
  }

  function openNotification(notification: NotificationItem) {
    if (notification.isRead === false) {
      markReadMutation.mutate(notification.id);
    }
    const navigationPath = resolveInAppNavigationPath(notification.actionUrl);
    if (navigationPath !== null) {
      navigate(navigationPath);
    }
  }

  function resolveEmptyStateMessage(): string {
    if (unreadOnly === true && selectedCategory !== null) {
      return t('center.emptyUnreadCategory');
    }
    if (unreadOnly === true) {
      return t('center.emptyUnread');
    }
    if (selectedCategory !== null) {
      return t('center.emptyCategory');
    }
    return t('center.empty');
  }

  return (
    <div className="p-4 sm:p-6 md:p-8 max-w-7xl mx-auto w-full">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 mb-1">
        <h1 className="text-2xl font-bold text-zooindigo">{t('center.title')}</h1>
        {unreadCount > 0 ? (
          <Button
            variant="outline"
            size="small"
            className="whitespace-nowrap"
            onClick={function markAll() {
              markAllReadMutation.mutate();
            }}
            isLoading={markAllReadMutation.isPending}
          >
            {t('center.markAllRead')}
          </Button>
        ) : null}
      </div>
      <p className="text-sm text-zoomuted mb-5">{t('center.subtitle')}</p>

      <div role="tablist" aria-label={t('center.title')} className="flex gap-2 mb-3">
        <FilterTab
          label={t('center.filters.all')}
          isActive={unreadOnly === false}
          onSelect={function showAll() {
            selectUnreadFilter(false);
          }}
        />
        <FilterTab
          label={t('center.filters.unread')}
          isActive={unreadOnly === true}
          onSelect={function showUnread() {
            selectUnreadFilter(true);
          }}
        />
      </div>

      <div className="mb-4">
        <CategoryFilterChips
          categories={resolveNotificationCategoriesForScope(activeScopeCode)}
          selectedCategory={selectedCategory}
          onSelectCategory={selectCategory}
        />
      </div>

      {notificationsQuery.isError === true ? (
        <QueryErrorState
          onRetry={function retryNotifications() {
            notificationsQuery.refetch();
          }}
        />
      ) : (
        <>
          <Card as="section" padding="none" className="overflow-hidden">
            {notificationsQuery.isLoading === true ? (
              <div className="py-12 flex justify-center">
                <LoadingSpinner className="w-6 h-6" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-12 flex flex-col items-center text-center px-4">
                <div className="w-12 h-12 rounded-full bg-zoovpalelavender flex items-center justify-center text-zooviolet">
                  <Icon name="Bell" />
                </div>
                <p className="mt-3 text-sm text-zoomuted">{resolveEmptyStateMessage()}</p>
              </div>
            ) : (
              <ul className="divide-y divide-zooborder">
                {DATE_GROUP_ORDER.map(function renderGroup(groupKey) {
                  const groupItems = groupedNotifications.get(groupKey);
                  if (groupItems === undefined || groupItems.length === 0) {
                    return null;
                  }
                  return (
                    <li key={groupKey}>
                      <div className="px-4 py-2 bg-zoooffwhite text-[11px] font-bold uppercase tracking-wider text-zoomuted">
                        {t('center.dateGroups.' + groupKey)}
                      </div>
                      <ul className="divide-y divide-zooborder">
                        {groupItems.map(function renderNotification(notification) {
                          return (
                            <NotificationRow
                              key={notification.id}
                              notification={notification}
                              onOpen={function open() {
                                openNotification(notification);
                              }}
                              onMarkAsRead={function markRead() {
                                markReadMutation.mutate(notification.id);
                              }}
                              isMarkingAsRead={
                                markReadMutation.isPending === true &&
                                markReadMutation.variables === notification.id
                              }
                            />
                          );
                        })}
                      </ul>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          <ListPagination
            page={page}
            pageSize={NOTIFICATION_CENTER_PAGE_SIZE_VALUE}
            totalItems={totalItems}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}

interface FilterTabProps {
  label: string;
  isActive: boolean;
  onSelect: () => void;
}

function FilterTab({ label, isActive, onSelect }: FilterTabProps) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      onClick={onSelect}
      className={cn(
        'px-4 py-2 rounded-xl text-sm font-medium transition-all',
        isActive
          ? 'bg-zooprimary text-zoowhite shadow-sm'
          : 'bg-zoowhite border border-zooborder text-zoomuted hover:border-zooprimary hover:text-zooprimary'
      )}
    >
      {label}
    </button>
  );
}
